import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import EventTypes from "../shared/EventTypes";

export default class MobileMenu extends AbstractProvidedClass {
    config = {
        'select': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#cursor-fill"></use></svg>',
            'callback': () => this.tools.setTool('select'),
        },
        'shapes': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#shape-line"></use></svg>',
            'callback': () => this.tools.setTool('shapes'),
        },
        'navigation': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#drag-move-2-fill"></use></svg>',
            'callback': () => this.tools.setTool('navigate'),
        },
        'erase': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#eraser-line"></use></svg>',
            'callback': () => this.tools.setTool('erase'),
        },
        'image': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#image-2-line"></use></svg>',
            'callback': () => this.tools.setTool('image'),
        },
        'settings': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#list-settings-fill"></use></svg>',
            'callback': () => this.tools.setTool('settings'),
        },
        'help': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#question-line"></use></svg>',
            'callback': () => this.tools.setTool('help'),
        },
        'reset': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#refresh-line"></use></svg>',
            'callback': () => this.view.reset(),
        },
        'save': {
            'icon': 'Save',
            'callback': () => this.dataHandler.savePlan(),
        },
        'undo': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#arrow-go-back-fill"></use></svg>',
            'callback': () => this.actions.undo(),
        },
        'redo': {
            'icon': '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#arrow-go-forward-fill"></use></svg>',
            'callback': () => this.actions.redo(),
        },
    };

    init(leftButtons, rightButtons) {
        this.container = this.p.createDiv()
            .parent('#p5canvas')
            .class('planner-toolbar-mobile')
            .style('display', 'flex')
            .style('flex-drection', 'row')
            .style('z-index', 2);

        let buttons = leftButtons.concat(rightButtons);

        this.createMobileMenu(buttons);

        this.on(EventTypes.OPEN_SIDEBAR, () => this.hide());
        this.on(EventTypes.CLOSE_SIDEBAR, () => this.show());
    }

    createMobileMenu(buttons) {
        // Tool buttons (select, shapes, navigate/navigation, erase, image)
        const toolButtons = ['select', 'shapes', 'navigation', 'erase', 'image'];
        // Action buttons (settings, help, reset, save)
        const actionButtons = ['settings', 'help', 'reset', 'save'];

        // Actions hamburger menu
        this.actionsMenu = this.createActionsMenu(buttons.filter(btn => actionButtons.includes(btn)));

        // Current tool dropdown (only when there are tools)
        const tools = buttons.filter(btn => toolButtons.includes(btn));
        if (tools.length > 0) {
            this.toolDropdown = this.createToolDropdown(tools);
        }

        // Undo/Redo + fullscreen container
        this.undoContainer = this.p.createDiv()
            .parent(this.container)
            .class('planner-toolbar-mobile-subcontainer');
        let undoContainer = this.undoContainer;

        if (buttons.includes('undo')) {
            this.undoButton = this.createMenuButton('undo', undoContainer);
        }
        if (buttons.includes('redo')) {
            this.redoButton = this.createMenuButton('redo', undoContainer);
        }

        if (document.fullscreenEnabled) {
            this.fullscreenButton = this.p.createButton(
                '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#fullscreen-line"></use></svg>'
            )
                .parent(undoContainer)
                .class('btn-icon-clear')
                .mousePressed(() => this.toggleFullscreen());

            document.addEventListener('fullscreenchange', () => {
                const icon = document.fullscreenElement ? 'fullscreen-exit-line' : 'fullscreen-line';
                this.fullscreenButton.html(`<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#${icon}"></use></svg>`);
            });
        }
    }

    toggleFullscreen() {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            document.documentElement.requestFullscreen();
        }
    }

    createToolDropdown(tools) {
        this.currentToolName = 'select'; // Default

        let dropdownContainer = this.p.createDiv()
            .class("planner-toolbar-mobile-subcontainer")
            .parent(this.container);

        this._toolDropdownElt = dropdownContainer.elt;

        let dropdown = this.p.createDiv()
            .parent(dropdownContainer)
            .class('tool-dropdown');

        // Button showing current tool — fixed size so it never resizes when icon changes
        this.currentButton = this.p.createButton(this.config[this.currentToolName]['icon'])
            .parent(dropdown)
            .class('btn-icon-clear')
            .style('position', 'relative')
            .mousePressed(() => this.toggleToolMenu());

        // Dropdown menu (hidden by default)
        let menu = this.p.createDiv()
            .parent(dropdown)
            .class('tool-menu')
            .hide();

        tools.forEach(tool => {
            let btn = this.p.createButton(this.config[tool]['icon'])
                .parent(menu)
                .class('btn-icon-clear')
                .style('display', 'flex')
                .style('align-items', 'center')
                .style('justify-content', 'center')
                .mousePressed(() => {
                    this.config[tool]['callback']();
                    this.currentToolName = tool;
                    this.currentButton.html(this.config[tool]['icon']);
                    menu.hide();
                });
            window[`${tool}Button`] = btn;
        });

        return { currentButton: this.currentButton, menu };
    }

    setCurrentToolIcon(html) {
        this.currentButton?.html(html);
    }

    resetCurrentToolIcon() {
        if (this.currentToolName && this.config[this.currentToolName]) {
            this.currentButton?.html(this.config[this.currentToolName]['icon']);
        }
    }

    createActionsMenu(actions) {
        let dropdownContainer = this.p.createDiv()
            .parent(this.container)
            .class("planner-toolbar-mobile-subcontainer");

        this._actionsDropdownElt = dropdownContainer.elt;

        let dropdown = this.p.createDiv()
            .parent(dropdownContainer)
            .class('actions-dropdown');

        // Hamburger button
        let hamburger = this.p.createButton('☰')
            .parent(dropdown)
            .class('btn-icon-clear')
            .mousePressed(() => this.toggleActionsMenu());

        // Dropdown menu (hidden by default)
        let menu = this.p.createDiv()
            .parent(dropdown)
            .class('actions-menu')
            .hide();

        actions.forEach(action => {
            let config = this.config[action];
            let btn = this.p.createButton(config['icon'])
                .parent(menu)
                .class('btn-icon-clear')
                .mousePressed(() => {
                    config['callback']();
                    menu.hide();
                });
            window[`${action}Button`] = btn;
        });

        return { hamburger, menu };
    }

    toggleToolMenu() {
        const isOpen = this.toolDropdown.menu.elt.style.display !== 'none';
        if (isOpen) {
            this.toolDropdown.menu.hide();
            if (this._toolMenuCloseHandler) {
                document.removeEventListener('touchstart', this._toolMenuCloseHandler);
                document.removeEventListener('mousedown', this._toolMenuCloseHandler);
                this._toolMenuCloseHandler = null;
            }
        } else {
            this.toolDropdown.menu.elt.style.display = 'flex';
            if (this.actionsMenu) this.actionsMenu.menu.hide();
            this._toolMenuCloseHandler = (e) => {
                if (!this._toolDropdownElt.contains(e.target)) {
                    this.toolDropdown.menu.hide();
                    document.removeEventListener('touchstart', this._toolMenuCloseHandler);
                    document.removeEventListener('mousedown', this._toolMenuCloseHandler);
                    this._toolMenuCloseHandler = null;
                }
            };
            setTimeout(() => {
                document.addEventListener('touchstart', this._toolMenuCloseHandler);
                document.addEventListener('mousedown', this._toolMenuCloseHandler);
            }, 0);
        }
    }

    toggleActionsMenu() {
        const isOpen = this.actionsMenu.menu.elt.style.display !== 'none';
        if (isOpen) {
            this.actionsMenu.menu.hide();
            if (this._actionsMenuCloseHandler) {
                document.removeEventListener('touchstart', this._actionsMenuCloseHandler);
                document.removeEventListener('mousedown', this._actionsMenuCloseHandler);
                this._actionsMenuCloseHandler = null;
            }
        } else {
            this.actionsMenu.menu.elt.style.display = 'flex';
            if (this.toolDropdown) this.toolDropdown.menu.hide();
            this._actionsMenuCloseHandler = (e) => {
                if (!this._actionsDropdownElt.contains(e.target)) {
                    this.actionsMenu.menu.hide();
                    document.removeEventListener('touchstart', this._actionsMenuCloseHandler);
                    document.removeEventListener('mousedown', this._actionsMenuCloseHandler);
                    this._actionsMenuCloseHandler = null;
                }
            };
            setTimeout(() => {
                document.addEventListener('touchstart', this._actionsMenuCloseHandler);
                document.addEventListener('mousedown', this._actionsMenuCloseHandler);
            }, 0);
        }
    }

    createNormalMenu(buttons) {
        this.container.class("planner-toolbar");
        buttons.map(btn => this.createMenuButton(btn));
    }

    createMenuButton(name, container) {
        let config = this.config[name];
        let buttonName = `${name}Button`;
        let button = this.p.createButton(config['icon'])
            .parent(container)
            .class('btn-icon-clear -u-tt-bottom')
            .mousePressed(config['callback']);

        if (['undo', 'redo'].includes(name)) {
            button.attribute('disabled', 'true');
        }
        window[buttonName] = button;
        return button;
    }

    showNewPlanSaveButton() {
        if (this._newPlanSaveBtn) return;
        this._newPlanSaveBtn = this.p.createButton('Save')
            .parent(this.undoContainer)
            .class('btn-icon-clear')
            .style('width', '35px')
            .mousePressed(() => this.dataHandler.savePlan());
    }

    showMultiSelectButton(onToggle) {
        if (this._multiSelectBtn) return;
        this._multiSelectActive = false;
        this._multiSelectContainer = this.p.createDiv()
            .parent(this.container)
            .class('planner-toolbar-mobile-subcontainer');
        this._multiSelectBtn = this.p.createButton(
            '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg#checkbox-multiple-line"></use></svg>'
        )
            .parent(this._multiSelectContainer)
            .class('btn-icon-clear')
            .mousePressed(() => {
                this._multiSelectActive = !this._multiSelectActive;
                if (this._multiSelectActive) {
                    this._multiSelectBtn.addClass('-is-active');
                } else {
                    this._multiSelectBtn.removeClass('-is-active');
                }
                onToggle(this._multiSelectActive);
            });
    }

    hideMultiSelectButton() {
        this._multiSelectContainer?.remove();
        this._multiSelectContainer = null;
        this._multiSelectBtn = null;
        this._multiSelectActive = false;
    }

    hide() {
        if (this.container.elt.offsetWidth + this.sideBar.width + this.sideBar.menuWidth > this.p.width) {
            this.container.hide();
        }
    }

    show() {
        this.container.show();
        this.container.style('display', null);
    }
}
